import {
  Box,
  Flex,
  FlexProps,
  Collapse,
  useDisclosure,
  Text,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { AvailableIcon, Icon } from 'src/components/atoms/Icon';
import { IconButton } from 'src/components/atoms/IconButton';
import { MMMRoute } from 'src/app/routes/utils';
import { useActiveRoute } from 'src/hooks/useActiveRoute';
import { Link } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';

type SidebarCollapsibleItemProps = FlexProps & {
  icon: AvailableIcon;
  route: MMMRoute;
  isActive?: boolean;
  children?: React.ReactNode;
  iconColor?: string;
  activeIconColor?: string;
  badgeCount?: number;
};

export const SidebarCollapsibleItem = ({
  icon,
  route,
  isActive = false,
  children,
  iconColor,
  activeIconColor,
  badgeCount,
  ...rest
}: SidebarCollapsibleItemProps) => {
  const { activeSubroute } = useActiveRoute();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isActive });

  const activeBg = useColorModeValue('green.50', 'green.900');
  const activeColor = useColorModeValue('green.600', 'green.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const subrouteHoverBg = useColorModeValue('gray.50', 'gray.600');
  const subrouteActiveBg = useColorModeValue('green.100', 'green.800');
  const activeTextColor = useColorModeValue('black', 'white');

  const hasSubroutes = route.subroutes && route.subroutes.length > 0;

  return (
    <Box mt="12">
      <Flex
        align="center"
        px="8"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        onClick={hasSubroutes ? onToggle : undefined}
        bg={isActive ? activeBg : 'transparent'}
        color={isActive ? activeColor : textColor}
        {...rest}>
        {icon && (
          <Icon
            mr="4"
            fontSize="16"
            iconName={icon}
            color={isActive ? 'icon.green' : 'icon.light.color'}
            w={6}
            h={6}
          />
        )}
        <Link
          as={NavLink}
          to={route.url}
          style={{ textDecoration: 'none' }}
          _activeLink={{
            color: activeColor,
          }}>
          <Flex align="center" gap={4}>
            <Text
              fontFamily="marketingPage"
              fontSize={16}
              flex="1"
              color={isActive ? activeTextColor : 'text.darkGray'}
              fontWeight={'medium'}>
              {route.name}
            </Text>
            {badgeCount !== undefined && badgeCount > 0 && (
              <Badge
                bg="icon.green"
                color="white"
                borderRadius="full"
                px={1.5}
                py={0.5}
                fontSize="xs"
                fontWeight="normal"
                fontFamily="marketingPage"
                minW="20px"
                textAlign="center">
                {badgeCount > 99 ? '99+' : badgeCount}
              </Badge>
            )}
          </Flex>
        </Link>
        {hasSubroutes && (
          <IconButton
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            icon={
              isOpen ? (
                <Icon iconName="chevron-down" boxSize="6" />
              ) : (
                <Icon iconName="chevron-right" boxSize="6" />
              )
            }
            variant="unstyled"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
        )}
      </Flex>

      {hasSubroutes && (
        <Collapse in={isOpen} animateOpacity>
          <Box ml="8" mt="4">
            {route.subroutes?.map((subroute) => {
              const isSubrouteActive = activeSubroute?.url === subroute.url;

              return (
                <Box key={subroute.url} pl="3">
                  <Link
                    as={NavLink}
                    to={subroute.url}
                    style={{ textDecoration: 'none' }}
                    _activeLink={{
                      bg: subrouteActiveBg,
                      color: activeColor,
                    }}>
                    <NavLink
                      to={subroute.url}
                      style={{ textDecoration: 'none' }}>
                      <Flex
                        align="center"
                        p="3"
                        pl="7"
                        borderLeft="1px solid"
                        borderColor="border.layout"
                        _hover={{
                          bg: subrouteHoverBg,
                          color: textColor,
                        }}
                        color={isSubrouteActive ? activeColor : textColor}>
                        <Text
                          fontFamily="marketingPage"
                          fontSize={16}
                          flex="1"
                          color={
                            isSubrouteActive ? activeTextColor : 'text.darkGray'
                          }
                          fontWeight={'medium'}>
                          {subroute.name}
                        </Text>
                      </Flex>
                    </NavLink>
                  </Link>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
